import { FontAwesome5 } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import React, { useState, useEffect } from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';

import Styles from '../../styles';
import Colors from '../../styles/Colors';
import { CalcVariants, Difficulties, QuestionScreenProps } from '../../types';
import { random, difficulties, isPrime } from '../../utils';
import Key from '../Key';

export default function QuestionScreen({ questionTypes }: QuestionScreenProps) {
  const [input, setInput] = useState('');
  const [answer, setAnswer] = useState<number | boolean>();
  const [isLoading, setLoading] = useState(true);
  const [questionType, setQuestionType] = useState<string>();
  const [questions, setQuestions] = useState<Record<number, number | boolean>>({
    1: false,
    2: false,
    3: false,
  });
  const [difficulty, setDifficulty] = useState<Difficulties>({
    plus: 'medium',
    minus: 'medium',
    times: 'medium',
    divide: 'medium',
  });

  useEffect(() => {
    const getData = async () => {
      try {
        const difficultyChoices = await AsyncStorage.getItem('Difficulty');
        if (difficultyChoices !== null) {
          setDifficulty(JSON.parse(difficultyChoices));
        }
        setLoading(false);
      } catch (e) {}
    };
    getData();
  }, []);

  useEffect(() => {
    newQuestion();
  }, [questionTypes, difficulty]);

  function backSpace() {
    setInput(input.slice(0, -1));
  }

  function enter() {
    if (answer == +input) {
      newQuestion();
    } else {
      setInput('');
    }
  }

  function handlePress(num: string) {
    setInput(input + num);
  }

  function newQuestion() {
    setInput('');
    const calcType: CalcVariants =
      questionTypes[Math.floor(Math.random() * questionTypes.length)];
    setQuestionType(calcType);
    const inputField = random(1, 3);
    let questionsTmp: Record<number, number | boolean>;
    let q1;
    let q2;
    const currentDifficulty = difficulties[calcType][difficulty[calcType]];

    switch (calcType) {
      case 'plus':
        q1 = random(currentDifficulty.min, currentDifficulty.max);
        q2 = random(currentDifficulty.min, currentDifficulty.max);
        questionsTmp = { 1: q1, 2: q2, 3: q1 + q2 };
        break;
      case 'minus':
        q1 = random(currentDifficulty.min, currentDifficulty.max);
        q2 = random(currentDifficulty.min, q1);
        questionsTmp = { 1: q1, 2: q2, 3: q1 - q2 };
        break;
      case 'times':
        q1 = random(currentDifficulty.min, currentDifficulty.max);
        q2 = random(currentDifficulty.min, currentDifficulty.max);
        questionsTmp = { 1: q1, 2: q2, 3: q1 * q2 };
        break;
      case 'divide':
        q1 = random(currentDifficulty.min, currentDifficulty.max);
        while (isPrime(q1)) {
          q1 = random(currentDifficulty.min, currentDifficulty.max);
        }

        q2 = random(currentDifficulty.min +1, q1 - 1);
        while (q1 % q2 !== 0) {
          q2 = random(currentDifficulty.min +1, q1 - 1);
        }
        questionsTmp = { 1: q1, 2: q2, 3: q1 / q2 };
        break;
    }
    setAnswer(questionsTmp[inputField]);
    setQuestions({ ...questionsTmp, [inputField]: false });
  }
  return (
    <View style={Styles.container}>
      {isLoading && (
        <ActivityIndicator color={Colors.buttonLight} size="large" />
      )}
      {!isLoading && (
        <>
          <View style={[styles.questionBox]}>
            <Text
              style={[styles.text, questions[1] === false && styles.answer]}
            >
              {questions[1] !== false ? questions[1] : input}
            </Text>
            <FontAwesome5 name={questionType} size={20} color={Colors.text} />
            <Text
              style={[styles.text, questions[2] === false && styles.answer]}
            >
              {questions[2] !== false ? questions[2] : input}
            </Text>
            <FontAwesome5 name="equals" size={20} color={Colors.text} />
            <Text
              style={[styles.text, questions[3] === false && styles.answer]}
            >
              {questions[3] !== false ? questions[3] : input}
            </Text>
          </View>
          <View>
            <View style={[styles.row]}>
              <Key onPress={handlePress} number="1" />
              <Key onPress={handlePress} number="2" />
              <Key onPress={handlePress} number="3" />
            </View>
            <View style={[styles.row]}>
              <Key onPress={handlePress} number="4" />
              <Key onPress={handlePress} number="5" />
              <Key onPress={handlePress} number="6" />
            </View>
            <View style={[styles.row]}>
              <Key onPress={handlePress} number="7" />
              <Key onPress={handlePress} number="8" />
              <Key onPress={handlePress} number="9" />
            </View>
            <View style={[styles.row]}>
              <Key onPress={handlePress} number="0" />
              <Key onPress={backSpace} number="backspace" icon />
              <Key onPress={enter} number="check" icon />
            </View>
            <View style={[styles.row, { justifyContent: 'center' }]}>
              <Key onPress={newQuestion} number="forward" icon />
            </View>
          </View>
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
  },
  questionBox: {
    justifyContent: 'space-evenly',
    alignItems: 'center',
    borderWidth: 3,
    borderColor: Colors.buttonLight,
    width: '80%',
    height: '25%',
    borderRadius: 15,
    flexDirection: 'row',
    marginBottom: 20,
  },
  text: {
    fontSize: 30,
    color: Colors.text,
    textAlign: 'center',
  },
  answer: {
    minWidth: 50,
    borderBottomWidth: 3,
    borderColor: Colors.buttonLight,
    height: 40,
  },
});
